import { HexBoard } from './hex/HexBoard';
import { HexTile } from './hex/HexTile';
import { HexCoord } from './hex/HexTypes';
import { RouteSearcherV4 } from './RouteSearcherV4';

export interface AnimationInfo {
  text: string;
  startTime: number;
  x: number;
  y: number;
}

export interface RoundTimer {
  startTime: number;
  endTime: number | null;
  elapsed: number;
}

export interface RoundState {
  round: number;
  boardSnapshot: Map<string, HexTile>;
  timer: RoundTimer;
  success: boolean;
}

export class MiniGameMode {
  private isActive: boolean = false;
  private board: HexBoard;
  private animatingTiles: Map<string, number> = new Map();
  private activeAnimations: AnimationInfo[] = [];
  private targetPattern: string[] = ['チム', 'チム', 'ドン', 'ドン', 'チム', 'ドン', 'ドン'];
  private currentPatternIndex: number = 0;
  private currentTile: HexCoord | null = null;
  private currentTileUsedCount: number = 0;
  private usedMarblesInCurrentTile: string[] = [];
  private hasUsedNewTile: boolean = false;  // 新規配置タイルを使用したかのフラグ
  private usedTilesInRoute: Set<string> = new Set();  // ルート全体で使用済みタイル
  
  // ミニゲーム用の新しいプロパティ
  private currentRound: number = 1;
  private maxRounds: number = 6;
  private roundStates: RoundState[] = [];
  private currentRoundTimer: RoundTimer | null = null;
  private roundStartSnapshot: Map<string, HexTile> = new Map();
  private routeSearcher: RouteSearcherV4;
  private penaltyTime: number = 0;  // ペナルティ時間（ミリ秒）
  private boardSeed: string = '';  // ボードのシード値
  private onGameComplete: ((totalTime: number, roundNumber: number, boardSeed: string, hintPenalty: number) => void) | null = null;
  private onRoundComplete: ((roundNumber: number) => void) | null = null;  // マルチプレイヤー用ラウンド完了コールバック
  
  constructor(board: HexBoard) {
    this.board = board;
    this.routeSearcher = new RouteSearcherV4(board);
  }
  
  /**
   * ゲーム完了時のコールバックを設定
   */
  public setOnGameComplete(callback: (totalTime: number, roundNumber: number, boardSeed: string, hintPenalty: number) => void): void {
    this.onGameComplete = callback;
  }
  
  public start(): void {
    this.isActive = true;
    this.currentRound = 1;
    this.roundStates = [];
    this.penaltyTime = 0;  // ペナルティ時間をリセット

    // 全ラウンドのルートが存在するまでボードを再生成
    this.ensureValidBoard();

    // ボードシードを生成（現在時刻をベースに）
    this.boardSeed = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.startNewRound();
  }

  /**
   * 全ラウンドでルートが存在するボードになるまで再生成
   */
  private ensureValidBoard(): void {
    const maxAttempts = 100;
    let attempts = 0;

    while (attempts < maxAttempts) {
      if (this.validateAllRounds()) {
        console.log(`Valid board found after ${attempts + 1} attempts`);
        return;
      }

      // ボードを再生成
      this.board.regenerate(this.board.getTileConfig());
      attempts++;
    }

    console.warn(`Could not find valid board after ${maxAttempts} attempts. Using current board.`);
  }

  /**
   * 全6ラウンドでルートが存在するか検証
   */
  private validateAllRounds(): boolean {
    for (let round = 0; round < 6; round++) {
      // キーマーカーを設定
      this.board.setKeyMarkerForRound(round);

      // ルートを探索（緑チップなし）
      this.routeSearcher.setMaxGreenChips(0);
      const routes = this.routeSearcher.findAllRoutes();

      if (routes.length === 0) {
        console.log(`No route found for round ${round + 1}`);
        // 検証後、状態をリセット
        this.board.resetRound();
        return false;
      }
    }

    // 検証成功、状態をリセット
    this.board.resetRound();
    return true;
  }
  
  public stop(): void {
    this.isActive = false;
    this.stopCurrentTimer();
    this.clearAnimations();
  }
  
  public isEnabled(): boolean {
    return this.isActive;
  }
  
  public getCurrentRound(): number {
    return this.currentRound;
  }
  
  public getMaxRounds(): number {
    return this.maxRounds;
  }
  
  public getRoundStates(): RoundState[] {
    return [...this.roundStates];
  }
  
  public getCurrentRoundTimer(): RoundTimer | null {
    return this.currentRoundTimer;
  }
  
  public getTotalElapsedTime(): number {
    let total = 0;
    for (const state of this.roundStates) {
      total += state.timer.elapsed;
    }
    if (this.currentRoundTimer) {
      const elapsed = this.currentRoundTimer.endTime 
        ? this.currentRoundTimer.elapsed 
        : Date.now() - this.currentRoundTimer.startTime;
      total += elapsed;
    }
    // ペナルティ時間を加算
    return total + this.penaltyTime;
  }
  
  public addHintPenalty(): void {
    const PENALTY_SECONDS = 15;
    this.penaltyTime += PENALTY_SECONDS * 1000;
  }
  
  public getPenaltyTime(): number {
    return this.penaltyTime;
  }
  
  private startNewRound(): void {
    // 新しいタイルを配置（緑チップなしでルートが存在することを確認）
    this.placeNewTileWithRouteCheck();
    
    // タイル配置後のボードの状態をスナップショット（失敗時はここに戻る）
    this.saveRoundSnapshot();
    
    // タイマー開始
    this.startCurrentTimer();
    
    // パターンをリセット
    this.resetPattern();
    
    // 新規配置タイルフラグをリセット
    this.hasUsedNewTile = false;
  }
  
  private placeNewTileWithRouteCheck(): void {
    // マルチプレイヤーモードの場合、boardDataから配置
    if (this.board.isMultiplayerMode()) {
      const placed = this.board.placeNextRoundFromData();
      if (!placed) {
        console.error('キーマーカーの設定に失敗しました（マルチプレイヤー）');
        return;
      }
      // マルチプレイヤーではルートチェックをスキップ（すでに検証済みのデータを使用）
      console.log(`[MiniGameMode] Round ${this.currentRound}: Using pre-determined key marker for multiplayer`);
      return;
    }

    // シングルプレイヤーモード: キーマーカーを次のタイルに設定
    const advanced = this.board.advanceRound();
    if (!advanced) {
      console.error('キーマーカーの設定に失敗しました');
      return;
    }

    // ルートが存在するか確認（デバッグ用）
    this.routeSearcher.setMaxGreenChips(0);
    const routes = this.routeSearcher.findAllRoutes();

    const newTileCoord = this.getNewTileCoord();
    if (routes.length > 0) {
      console.log(`ラウンド${this.currentRound}: キータイル(${newTileCoord.x},${newTileCoord.y},${newTileCoord.z})を通るルートを${routes.length}個発見`);
    } else {
      console.warn(`ラウンド${this.currentRound}: キータイル(${newTileCoord.x},${newTileCoord.y},${newTileCoord.z})を通るルートが見つかりません`);
    }
  }
  
  private saveRoundSnapshot(): void {
    this.roundStartSnapshot.clear();
    this.board.getAllTiles().forEach(tile => {
      const clonedTile = this.cloneTile(tile);
      this.roundStartSnapshot.set(clonedTile.id, clonedTile);
    });
  }
  
  private cloneTile(tile: HexTile): HexTile {
    const cloned = new HexTile(tile.coords, tile.ring);
    cloned.setTileType(tile.getTileType());
    return cloned;
  }
  
  private restoreRoundSnapshot(): void {
    if (this.roundStartSnapshot.size === 0) return;
    
    // ボードの状態を復元
    this.roundStartSnapshot.forEach((snapshotTile, id) => {
      const currentTile = this.board.getTileById(id);
      if (currentTile) {
        currentTile.setTileType(snapshotTile.getTileType());
      }
    });
  }
  
  private startCurrentTimer(): void {
    this.currentRoundTimer = {
      startTime: Date.now(),
      endTime: null,
      elapsed: 0
    };
  }
  
  private stopCurrentTimer(): void {
    if (this.currentRoundTimer && !this.currentRoundTimer.endTime) {
      this.currentRoundTimer.endTime = Date.now();
      this.currentRoundTimer.elapsed = this.currentRoundTimer.endTime - this.currentRoundTimer.startTime;
    }
  }
  
  private resetPattern(): void {
    this.currentPatternIndex = 0;
    this.currentTile = null;
    this.currentTileUsedCount = 0;
    this.usedMarblesInCurrentTile = [];
    this.hasUsedNewTile = false;
    this.usedTilesInRoute.clear();
  }
  
  private clearAnimations(): void {
    this.animatingTiles.clear();
    this.activeAnimations = [];
  }
  
  public getCurrentPatternProgress(): string {
    return `${this.currentPatternIndex} / ${this.targetPattern.length}`;
  }
  
  /**
   * 新規配置タイルの座標を取得
   */
  public getNewTileCoord(): HexCoord {
    const cornerCoords: HexCoord[] = [
      { x: 0, y: 2, z: -2 },   // ラウンド1
      { x: 2, y: 0, z: -2 },   // ラウンド2
      { x: 2, y: -2, z: 0 },   // ラウンド3
      { x: 0, y: -2, z: 2 },   // ラウンド4
      { x: -2, y: 0, z: 2 },   // ラウンド5
      { x: -2, y: 2, z: 0 }    // ラウンド6
    ];
    return cornerCoords[(this.currentRound - 1) % 6];
  }
  
  public getNextExpectedText(): string {
    if (this.currentPatternIndex < this.targetPattern.length) {
      return this.targetPattern[this.currentPatternIndex];
    }
    return '';
  }
  
  public getCurrentPatternIndex(): number {
    return this.currentPatternIndex;
  }
  
  /**
   * タイルクリック処理
   */
  public handleTileClick(tile: HexCoord, pixelX: number, pixelY: number): string | null {
    if (!this.isActive) return null;
    
    const tileData = this.board.getTileByCoords(tile);
    if (!tileData || tileData.getTileType() === 'EMPTY') return null;
    
    // 新規配置タイルかチェック
    const newTileCoord = this.getNewTileCoord();
    const isNewTile = tile.x === newTileCoord.x && tile.y === newTileCoord.y && tile.z === newTileCoord.z;
    if (isNewTile) {
      this.hasUsedNewTile = true;
    }
    
    // 現在使用中のタイルがある場合、同じタイルをクリックしているかチェック
    if (this.currentTile) {
      const isSameTile = this.currentTile.x === tile.x && 
                        this.currentTile.y === tile.y && 
                        this.currentTile.z === tile.z;
      
      console.log('=== Tile Switch Check ===');
      console.log('CurrentTile:', `(${this.currentTile.x},${this.currentTile.y},${this.currentTile.z})`);
      console.log('NewTile:', `(${tile.x},${tile.y},${tile.z})`);
      console.log('IsSameTile:', isSameTile);
      
      if (!isSameTile) {
        // 違うタイルをクリックした場合、前のタイルの丸を使い切っているかチェック
        const previousTileData = this.board.getTileByCoords(this.currentTile);
        if (previousTileData) {
          // 旗頭タイルの場合は1個使えばOK
          const flagshipTile = this.board.getFlagshipTile();
          const isFlagship = flagshipTile && 
            flagshipTile.coords.x === previousTileData.coords.x &&
            flagshipTile.coords.y === previousTileData.coords.y &&
            flagshipTile.coords.z === previousTileData.coords.z;
          const previousMarbles = this.getMarbleTexts(previousTileData);
          const requiredUsage = isFlagship ? 1 : previousMarbles.length;
          
          console.log('Previous tile check:');
          console.log('  IsFlagship:', isFlagship);
          console.log('  PreviousMarbles:', previousMarbles);
          console.log('  RequiredUsage:', requiredUsage);
          console.log('  CurrentTileUsedCount:', this.currentTileUsedCount);
          console.log('  NeedToUseAll?:', this.currentTileUsedCount < requiredUsage);
          
          if (this.currentTileUsedCount < requiredUsage) {
            console.log('=> FAILURE: Previous tile not fully used');
            console.log('=== Tile Switch Check END (FAILURE) ===\n');
            this.handleRoundFailure(pixelX, pixelY);
            return '失敗';
          }
        }
      }
      console.log('=== Tile Switch Check END (OK) ===\n');
    }
    
    // タイルのマーブル処理（既存のロジックを維持）
    const result = this.processTileMarble(tile, tileData, pixelX, pixelY);
    
    // 失敗の場合はそのまま返す
    if (result === '失敗') {
      return result;
    }
    
    // パターン完成チェック（成功の場合のみ）
    if (this.currentPatternIndex >= this.targetPattern.length) {
      // 最後のタイルが完全に使用されているかチェック
      if (this.currentTile) {
        const currentTileData = this.board.getTileByCoords(this.currentTile);
        if (currentTileData) {
          const flagshipTile = this.board.getFlagshipTile();
          const isFlagship = flagshipTile && 
            flagshipTile.coords.x === currentTileData.coords.x &&
            flagshipTile.coords.y === currentTileData.coords.y &&
            flagshipTile.coords.z === currentTileData.coords.z;
          const currentMarbles = this.getMarbleTexts(currentTileData);
          const requiredUsage = isFlagship ? 1 : currentMarbles.length;
          
          if (this.currentTileUsedCount >= requiredUsage) {
            // 新規配置タイルを通ったかチェック
            if (!this.hasUsedNewTile) {
              console.log('Pattern complete but new tile not used');
              this.handleRoundFailure(pixelX, pixelY);
              return '失敗';
            }
            // タイルを使い切った場合のみ成功
            this.handleRoundSuccess();
            return '成功';
          }
          // このケースは発生しないはず（processTileMarbleで既に失敗判定されている）
        }
      }
    }
    
    return result;
  }
  
  private processTileMarble(tile: HexCoord, tileData: HexTile, pixelX: number, pixelY: number): string | null {
    const expectedText = this.targetPattern[this.currentPatternIndex];

    console.log('=== processTileMarble START ===');
    console.log('Tile:', `(${tile.x},${tile.y},${tile.z})`);
    console.log('TileType:', tileData.getTileType());
    console.log('ExpectedText:', expectedText);
    console.log('CurrentPatternIndex:', this.currentPatternIndex);
    console.log('CurrentTile:', this.currentTile);
    console.log('CurrentTileUsedCount:', this.currentTileUsedCount);
    console.log('UsedMarblesInCurrentTile:', this.usedMarblesInCurrentTile);

    const isNewTile = !this.currentTile ||
                     this.currentTile.x !== tile.x ||
                     this.currentTile.y !== tile.y ||
                     this.currentTile.z !== tile.z;

    console.log('IsNewTile:', isNewTile);

    if (isNewTile) {
      // 既に使用済みタイルかチェック
      const tileKey = `${tile.x},${tile.y},${tile.z}`;
      if (this.usedTilesInRoute.has(tileKey)) {
        console.log('=> FAILURE! Tile already used in this route');
        console.log('=== processTileMarble END (FAILURE - duplicate tile) ===\n');
        this.handleRoundFailure(pixelX, pixelY);
        return '失敗';
      }

      // 隣接チェック（初回以外）
      if (this.currentTile) {
        const currentTileData = this.board.getTileByCoords(this.currentTile);
        if (currentTileData && !currentTileData.isNeighbor(tileData)) {
          console.log('=> FAILURE! Not adjacent to previous tile');
          console.log('=== processTileMarble END (FAILURE - not adjacent) ===\n');
          this.handleRoundFailure(pixelX, pixelY);
          return '失敗';
        }
      }

      console.log('=> Setting as new current tile');
      this.currentTile = tile;
      this.currentTileUsedCount = 0;
      this.usedMarblesInCurrentTile = [];
      this.usedTilesInRoute.add(tileKey);
    }

    // 旗頭タイルかどうかチェック（isNewTileの処理後に判定）
    const flagshipTile = this.board.getFlagshipTile();
    const isFlagship = flagshipTile &&
      flagshipTile.coords.x === tileData.coords.x &&
      flagshipTile.coords.y === tileData.coords.y &&
      flagshipTile.coords.z === tileData.coords.z;

    // タイルから利用可能なテキストを取得
    let availableTexts = this.getMarbleTexts(tileData);

    // 旗頭タイルの場合の特別処理
    if (isFlagship) {
      // 旗頭タイルは1回しかタップできない
      // 現在のパターンに合わせて1個だけ使える
      if (this.currentTileUsedCount > 0) {
        // 既に1回使っている場合は、使用不可
        availableTexts = [];
      } else {
        // 1回目は任意の色として使える
        availableTexts = ['チム', 'ドン'];
      }
    }

    console.log('IsFlagship:', isFlagship);
    console.log('AvailableTexts:', availableTexts);
    
    // 使用可能なテキストから期待されるテキストを探す
    const remainingTexts = [...availableTexts];
    for (const usedText of this.usedMarblesInCurrentTile) {
      const index = remainingTexts.indexOf(usedText);
      if (index >= 0) {
        remainingTexts.splice(index, 1);
      }
    }
    
    console.log('RemainingTexts after removing used:', remainingTexts);
    console.log('Expected text in remaining?:', remainingTexts.includes(expectedText));
    
    if (remainingTexts.includes(expectedText)) {
      // 成功
      console.log('=> SUCCESS! Pattern matched');
      this.currentPatternIndex++;
      this.currentTileUsedCount++;
      this.usedMarblesInCurrentTile.push(expectedText);
      
      console.log('After success - PatternIndex:', this.currentPatternIndex);
      console.log('After success - TileUsedCount:', this.currentTileUsedCount);
      console.log('After success - UsedMarbles:', this.usedMarblesInCurrentTile);
      
      // パターン完成時に最後のタイルをチェック
      if (this.currentPatternIndex >= this.targetPattern.length) {
        // 旗頭タイルかどうかチェック
        const flagshipTile = this.board.getFlagshipTile();
        const isCurrentFlagship = flagshipTile && 
          flagshipTile.coords.x === tile.x &&
          flagshipTile.coords.y === tile.y &&
          flagshipTile.coords.z === tile.z;
        const requiredUsage = isCurrentFlagship ? 1 : availableTexts.length;
        
        console.log('=== Pattern Complete - Final Tile Check ===');
        console.log('CurrentTile:', `(${tile.x},${tile.y},${tile.z})`);
        console.log('IsFlagship:', isCurrentFlagship);
        console.log('AvailableTexts:', availableTexts);
        console.log('RequiredUsage:', requiredUsage);
        console.log('CurrentTileUsedCount:', this.currentTileUsedCount);
        
        if (this.currentTileUsedCount < requiredUsage) {
          console.log('=> Pattern complete but last tile not fully used - FAILURE!');
          console.log('=== Pattern Complete - Final Tile Check END (FAILURE) ===\n');
          // パターンは完成したが、最後のタイルを使い切っていない → 即座に失敗
          this.handleRoundFailure(pixelX, pixelY);
          console.log('=== processTileMarble END (FAILURE - tile not fully used) ===\n');
          return '失敗';
        } else {
          console.log('=> Pattern complete and last tile fully used!');
          console.log('=== Pattern Complete - Final Tile Check END (SUCCESS) ===\n');
        }
      }
      
      // アニメーション
      this.activeAnimations.push({
        text: expectedText,
        startTime: Date.now(),
        x: pixelX,
        y: pixelY
      });
      
      console.log('=== processTileMarble END (SUCCESS) ===\n');
      return expectedText;
    } else {
      // 失敗
      console.log('=> FAILURE! Pattern not matched');
      console.log('Reason: Expected', expectedText, 'not in', remainingTexts);
      console.log('=== processTileMarble END (FAILURE) ===\n');
      this.handleRoundFailure(pixelX, pixelY);
      return '失敗';
    }
  }
  
  private handleRoundSuccess(): void {
    // タイマー停止
    this.stopCurrentTimer();
    
    // ラウンド結果を保存
    if (this.currentRoundTimer) {
      this.roundStates.push({
        round: this.currentRound,
        boardSnapshot: new Map(this.roundStartSnapshot),
        timer: { ...this.currentRoundTimer },
        success: true
      });
      // タイマーをクリアして、次のラウンド開始まで合計時間が正しく計算されるようにする
      this.currentRoundTimer = null;
    }
    
    // マルチプレイヤー用コールバックを呼ぶ
    if (this.onRoundComplete) {
      this.onRoundComplete(this.currentRound);
    }
    
    // 次のラウンドへ
    if (this.currentRound < this.maxRounds) {
      this.currentRound++;
      setTimeout(() => this.startNewRound(), 1000);
    } else {
      // ゲーム完了（6ラウンド目も履歴に含まれている）
      // currentRoundをインクリメントして、isGameComplete()がtrueを返すようにする
      this.currentRound++;
      this.handleGameComplete();
    }
  }
  
  /**
   * ラウンド完了コールバックを設定（マルチプレイヤー用）
   */
  public setOnRoundComplete(callback: (roundNumber: number) => void): void {
    this.onRoundComplete = callback;
  }
  
  /**
   * 外部からラウンドを進める（他プレイヤーがラウンドをクリアした時）
   */
  public advanceToNextRound(): void {
    if (this.currentRound < this.maxRounds) {
      this.currentRound++;
      this.startNewRound();
    }
  }
  
  private handleRoundFailure(pixelX: number, pixelY: number): void {
    // 失敗アニメーション
    this.activeAnimations.push({
      text: '失敗：やり直し',
      startTime: Date.now(),
      x: pixelX,
      y: pixelY
    });
    
    // 即座にパターンをリセット（重複クリック防止）
    this.resetPattern();
    
    // ボード状態は少し遅れて復元（視覚的フィードバックのため）
    setTimeout(() => {
      this.restoreRoundSnapshot();
    }, 1000);
  }
  
  private handleGameComplete(): void {
    this.isActive = false;
    
    // 合計時間を計算
    const totalTime = this.getTotalElapsedTime();
    
    // ゲーム完了コールバックを呼ぶ
    if (this.onGameComplete) {
      this.onGameComplete(totalTime, this.currentRound - 1, this.boardSeed, this.penaltyTime);
    }
  }
  
  private getMarbleTexts(tile: HexTile): string[] {
    const tileType = tile.getTileType();
    const texts: string[] = [];
    
    switch (tileType) {
      case 'BLUE_1':
      case 'BLUE_1_STAR':
        texts.push('ドン');
        break;
      case 'RED_1':
      case 'RED_1_STAR':
        texts.push('チム');
        break;
      case 'BLUE_2':
      case 'BLUE_2_STAR':
        texts.push('ドン', 'ドン');
        break;
      case 'RED_2':
      case 'RED_2_STAR':
        texts.push('チム', 'チム');
        break;
      case 'BLUE_1_RED_1':
      case 'BLUE_1_RED_1_STAR':
        texts.push('ドン', 'チム');
        break;
      case 'BLUE_2_RED_1':
      case 'BLUE_2_RED_1_STAR':
        texts.push('ドン', 'ドン', 'チム');
        break;
      case 'BLUE_1_RED_2':
      case 'BLUE_1_RED_2_STAR':
        texts.push('ドン', 'チム', 'チム');
        break;
    }
    
    return texts;
  }
  
  public getActiveAnimations(): AnimationInfo[] {
    // 古いアニメーションをフィルタリング
    const now = Date.now();
    this.activeAnimations = this.activeAnimations.filter(anim => 
      now - anim.startTime < 1000
    );
    return [...this.activeAnimations];
  }
  
  public isGameComplete(): boolean {
    return this.currentRound > this.maxRounds;
  }

  /**
   * 有効なタイルタイプかチェック（チムまたはドンのみ、混合タイプは不可）
   */
  private isValidTileType(tileType: string): boolean {
    const validTypes = [
      'BLUE_1', 'BLUE_1_STAR',
      'RED_1', 'RED_1_STAR',
      'BLUE_2', 'BLUE_2_STAR',
      'RED_2', 'RED_2_STAR'
    ];
    return validTypes.includes(tileType);
  }

  /**
   * RouteSearcherのルートをシミュレートしてテスト（デバッグ用）
   */
  public simulateRoute(route: HexTile[]): { success: boolean; failReason?: string } {
    console.log('\n=== Route Simulation Start ===');
    console.log('Route tiles:', route.map(t => `(${t.coords.x},${t.coords.y},${t.coords.z})`).join(' -> '));

    const testPattern = ['チム', 'チム', 'ドン', 'ドン', 'チム', 'ドン', 'ドン'];
    let patternIndex = 0;
    const usedTiles = new Set<string>();
    let prevTile: HexTile | null = null;

    for (const tile of route) {
      const tileKey = `${tile.coords.x},${tile.coords.y},${tile.coords.z}`;

      // 重複チェック
      if (usedTiles.has(tileKey)) {
        return { success: false, failReason: `Duplicate tile: ${tileKey}` };
      }

      // 隣接チェック
      if (prevTile && !prevTile.isNeighbor(tile)) {
        return { success: false, failReason: `Not adjacent: ${prevTile.coords.x},${prevTile.coords.y},${prevTile.coords.z} -> ${tileKey}` };
      }

      usedTiles.add(tileKey);
      prevTile = tile;
    }

    console.log('=== Route Simulation Success ===\n');
    return { success: true };
  }
}